import Swiper from '~/ui/components/common/Swiper/Swiper';
import { Link, Loader, Prices } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { useTranslation } from 'next-i18next';
import { ReactElement, useEffect, useState } from 'react';
import { useItemListName } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import {
  dataLayerFeaturedProductsAccor,
  dataLayerSelectItemAccor,
} from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { IFeaturedProduct } from '@Hotels-at-Home/hah-fe-core/dist/types/product';
import { getPrices, IPrices } from '@Hotels-at-Home/hah-fe-core/dist/utils/price';
import { getProductLinkFromUrlRewrites } from '@Hotels-at-Home/hah-fe-core/dist/utils/product';
import { accorTagging, parentHotelCompany, siteName } from '~/constants/siteConfig';
import { useRouter } from 'next/router';

import { IYmalItem } from '@Hotels-at-Home/hah-fe-core/dist/types/ymal';
import { useSelector } from '~/store/hooks';
import { selectYmals } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/ymal';

interface IProps {
  ymalName: string;
  staticOnDesktop?: boolean;
  sliceArray?: number;
  sliceBeginning?: number;
  isHomepageList?: boolean;
}

const CommonSwiper = ({
  ymalName,
  staticOnDesktop = false,
  sliceArray,
  sliceBeginning = 0,
  isHomepageList = false,
}: IProps) => {
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });

  const [isYmalLoading, setIsYmalLoading] = useState(true);
  const [ymalResponse, setYmalResponse] = useState<IYmalItem[]>(null);
  const itemListName = useItemListName();
  const [swiperKey, setSwiperKey] = useState(0);
  const ymals = useSelector(selectYmals);
  const { locale, push } = useRouter();

  useEffect(() => {
    if (ymals.ymals.length > 0) {
      setYmalResponse(ymals.ymals);
      setIsYmalLoading(false);
    }
  }, [ymals]);

  useEffect(() => {
    if (!isHomepageList) {
      return;
    }
    if (accorTagging.accorTaggingEnabled) {
      if (!ymalResponse || !ymalResponse.length) return;

      const ymalList = ymalResponse.find(ymalItem => ymalItem.name === ymalName).related_products;
      dataLayerFeaturedProductsAccor({
        brand: siteName,
        items: ymalList,
        itemListName,
      });
    }
  }, [ymalResponse]);

  const cartYmal = ymalResponse?.find(ymalItem => ymalItem.name === ymalName);
  let ymalArray = cartYmal ? cartYmal.related_products : [];
  if (sliceArray) {
    ymalArray = ymalArray.slice(sliceBeginning, sliceArray);
  }

  type GuestSlide = {
    name: string;
    image: string;
    prices: IPrices;
    link: string;
    id: string;
    index: number;
    product: IFeaturedProduct;
    short_description: string;
  };

  const guestSlides: GuestSlide[] = ymalArray.map((item, idx) => ({
    name: item.name,
    image: item.thumbnail.url,
    prices: getPrices(item.price_range, locale),
    link: getProductLinkFromUrlRewrites(item.url_rewrites, locale),
    id: item.name,
    index: idx,
    product: item,
    short_description: item.short_description ? item.short_description.html : '',
  }));

  const handleSlideClick = (slide: GuestSlide) => {
    if (accorTagging.accorTaggingEnabled) {
      dataLayerSelectItemAccor({
        product: slide.product,
        affiliation: parentHotelCompany,
        brand: siteName,
        index: slide.index,
        itemListName,
      });
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleResize = () => {
      setSwiperKey(prevKey => prevKey + 1);
    };

    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.addEventListener('change', handleResize);
    };
  }, []);

  const renderSlide = (slide: GuestSlide): ReactElement => (
    <Link
      href={slide.link}
      noClass
      data-event-name="bloc_interact"
      data-bloc-name={slide.name}
      onClick={() => handleSlideClick(slide)}
    >
      <div className="hover-btn-animation">
        <img src={slide.image} alt={slide.name} width={267} height={249} loading="lazy" />
        <div className="hover-content desktop-only">
          <span className="btn base-btn btn-main">
            <span>{tButton('shopNow')}</span>
          </span>
        </div>
      </div>
      <h3>{slide.name}</h3>
      {slide.prices.newPrice && <Prices prices={slide.prices} show />}
      {slide.short_description && <p className="description">{slide.short_description}</p>}
      <div>
        <button
          type="button"
          className="btn-plain"
          tabIndex={-1}
          aria-label={`${tButton('shopNow')}: ${slide.name}`}
          aria-disabled="false"
        >
          {tButton('shopNow')}
        </button>
      </div>
    </Link>
  );
  return isYmalLoading ? (
    <Loader />
  ) : (
    <Swiper
      key={swiperKey}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      slides={guestSlides}
      arrowBorder
      renderSlide={renderSlide}
      slideClassName={() => ''}
      navigation // Include navigation
      pagination // Include pagination
      containerModifierClass="swiper-container-"
      className="swiper-container swiper-guest swiper-wrapper"
      watchOverflow
      // Default values - making mobile as default
      slidesPerView={1.5}
      /* slidesPerGroup={1} */ // Move 'X' slides at a time
      spaceBetween={30} // Add space between slides
      touchEventsTarget="container"
      breakpoints={{
        // when window width is >= 768px
        768: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 25,
          allowTouchMove: !staticOnDesktop,
        },
      }}
    />
  );
};

export default CommonSwiper;
